import { useEffect, useRef, type ReactNode } from "react";

function stepDecimals(step: number): number {
  const text = String(step);
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : text.length - dot - 1;
}

export function nudgeRangeValue(
  current: number,
  min: number,
  max: number,
  step: number,
  direction: 1 | -1
): number {
  const next = min + Math.round((current + direction * step - min) / step) * step;
  const rounded = Number(next.toFixed(stepDecimals(step)));
  return Math.min(max, Math.max(min, rounded));
}

interface Props {
  min: number;
  max: number;
  step?: number;
  value: number;
  onValueChange: (value: number) => void;
  className?: string;
  children: ReactNode;
}

/**
 * While the pointer is over this region, the mouse wheel nudges `value`
 * by `step` (scroll up increases, scroll down decreases).
 */
export default function WheelHoverZone({
  min,
  max,
  step = 1,
  value,
  onValueChange,
  className,
  children,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    let leftover = 0;
    const onWheel = (e: WheelEvent) => {
      const raw =
        e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
      if (!raw) return;
      e.preventDefault();
      e.stopPropagation();

      leftover += raw;
      const unit = 80;
      let dirSum = 0;
      while (leftover >= unit) {
        leftover -= unit;
        dirSum -= 1;
      }
      while (leftover <= -unit) {
        leftover += unit;
        dirSum += 1;
      }
      if (!dirSum) return;

      const dir: 1 | -1 = dirSum > 0 ? 1 : -1;
      let next = valueRef.current;
      for (let i = 0; i < Math.abs(dirSum); i += 1) {
        next = nudgeRangeValue(next, min, max, step, dir);
      }
      if (next !== valueRef.current) onValueChangeRef.current(next);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [min, max, step]);

  return (
    <div ref={hostRef} className={className} title="Scroll to adjust">
      {children}
    </div>
  );
}
